import { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { processEmailForDirectRender } from "../utils/emailContentProcessor";

/**
 * DOMPurify configuration for email HTML sanitization
 * Preserves email-specific attributes and styles
 */
const getEmailSanitizeConfig = () => ({
  // Allow style, meta, and link tags (for embedded styles and meta tags)
  ADD_TAGS: ["style", "meta", "link"],
  // Add email-specific and HTML attributes that might be stripped
  ADD_ATTR: [
    // Standard attributes
    "target",
    "role",
    "aria-label",
    "aria-describedby",
    "dir",
    "lang",
    // Table attributes
    "bgcolor",
    "cellpadding",
    "cellspacing",
    "align",
    "valign",
    "width",
    "height",
    "border",
    "border-collapse",
    "colspan",
    "rowspan",
    "nowrap",
    "scope",
    "headers",
    // Image attributes
    "usemap",
    "ismap",
    // Link attributes
    "rel",
    "hreflang",
    "type",
    "download",
    // Form attributes (if emails contain forms)
    "name",
    "value",
    "checked",
    "selected",
    "disabled",
    "readonly",
    "placeholder",
    "required",
    // Microsoft Office/Outlook specific attributes (mso-*)
    // These are commonly used in Outlook emails
    /^mso-/,
    /^v:/,
    /^o:/,
    // Other email client specific attributes
    "data-saferedirecturl",
    "data-mce-href",
  ],
  KEEP_CONTENT: true,
  ALLOW_DATA_ATTR: true, // Allow all data-* attributes
  // Preserve inline styles which are critical for email rendering
  ALLOW_UNKNOWN_PROTOCOLS: false,
  // Allow style attributes on all elements (critical for email HTML)
  // This list includes common HTML attributes plus email-specific ones
  ALLOWED_ATTR: [
    // Core attributes
    "style", // CRITICAL: Inline styles are essential for email rendering
    "class",
    "id",
    // Link attributes
    "href",
    "target",
    "rel",
    "hreflang",
    "type",
    "download",
    // Image attributes
    "src",
    "alt",
    "title",
    "width",
    "height",
    "usemap",
    "ismap",
    // Table attributes
    "border",
    "cellpadding",
    "cellspacing",
    "bgcolor",
    "align",
    "valign",
    "colspan",
    "rowspan",
    "nowrap",
    "scope",
    "headers",
    // Form attributes
    "name",
    "value",
    "checked",
    "selected",
    "disabled",
    "readonly",
    "placeholder",
    "required",
    // Accessibility
    "role",
    "aria-label",
    "aria-describedby",
    "aria-hidden",
    "aria-live",
    // Language/direction
    "dir",
    "lang",
  ],
});

/**
 * Custom hook for processing and rendering email content
 * Handles extraction of body content, style injection, and HTML sanitization
 * 
 * @param {string} originalContent - Raw HTML content from email API
 * @param {boolean} isDraft - Whether the email is a draft (drafts use editor, not direct rendering)
 * @param {boolean} removeMetadata - Whether to remove initial metadata paragraphs (default: false)
 * @param {string} styleIdPrefix - Prefix for unique style tag ID (default: "email-content-styles")
 * @returns {Object} - Object containing sanitizedContent, processedContent, emailContentRef, and loading state
 * 
 * @example
 * const { sanitizedContent, emailContentRef } = useEmailContentRenderer(
 *   currentEmailId?.OriginalContent,
 *   isDraft,
 *   false,
 *   "email-detail-popup-styles"
 * );
 */
export const useEmailContentRenderer = (
  originalContent,
  isDraft = false,
  removeMetadata = false,
  styleIdPrefix = "email-content-styles"
) => {
  const [processedContent, setProcessedContent] = useState("");
  const [emailStyles, setEmailStyles] = useState("");
  const [sanitizedContent, setSanitizedContent] = useState("");
  const emailContentRef = useRef(null);
  const styleIdRef = useRef(`${styleIdPrefix}-${Date.now()}`);

  /**
   * Process email content when OriginalContent changes
   * Extracts body content and styles from full HTML documents
   * For read-only email viewing, we use direct HTML rendering instead of KendoEditor
   */
  useEffect(() => {
    if (!isDraft && originalContent) {
      // Process content for direct HTML rendering (preserves email styles)
      const { bodyContent, styles } = processEmailForDirectRender(originalContent, removeMetadata);

      // Sanitize HTML but preserve email-specific attributes and styles
      const sanitizeConfig = getEmailSanitizeConfig();
      const sanitized = DOMPurify.sanitize(bodyContent, sanitizeConfig);

      setSanitizedContent(sanitized);
      setEmailStyles(styles);
      setProcessedContent(bodyContent);
    } else {
      setProcessedContent("");
      setSanitizedContent("");
      setEmailStyles("");
    }
  }, [originalContent, isDraft, removeMetadata]);

  /**
   * Inject email styles into document head
   * Styles are scoped to this component instance
   */
  useEffect(() => {
    const styleId = styleIdRef.current;
    
    // Remove existing style tag if any
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Inject new styles if available
    if (emailStyles) {
      const styleTag = document.createElement("style");
      styleTag.id = styleId;
      styleTag.type = "text/css";
      styleTag.innerHTML = emailStyles;
      document.head.appendChild(styleTag);
    }

    // Cleanup on unmount or when styles change
    return () => {
      const styleTag = document.getElementById(styleId);
      if (styleTag) {
        styleTag.remove();
      }
    };
  }, [emailStyles]);

  return {
    sanitizedContent: sanitizedContent || processedContent || originalContent || "",
    processedContent: processedContent || originalContent || "",
    emailContentRef,
  };
};
